import { useEffect, useState, useCallback, useMemo } from 'react';
import { storyPointAPI } from '../../api/services';
import { useAppSelector } from '../../store/hooks';

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
const fmtD = (d?: string) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PAGE_SIZE = 10;

interface DeptBoardData {
  departmentName: string;
  totalMembers: number;
  totalSP: number;
  members: any[];
}

export default function ProjectBoard() {
  const { user } = useAppSelector(s => s.auth);
  const currentUserId = user?.id || user?._id;

  const [data, setData] = useState<DeptBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [memberEntries, setMemberEntries] = useState<any[]>([]);
  const [memberProfile, setMemberProfile] = useState<any>(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // Award SP
  const isAdminOrHOD = (user as any)?.role === 'admin' || ((user as any)?.organizationRole?.priority ?? 0) >= 50;
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [awardTarget, setAwardTarget] = useState<any>(null);
  const [awardPoints, setAwardPoints] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [awardSaving, setAwardSaving] = useState(false);
  const [awardError, setAwardError] = useState('');
  const [awardSuccess, setAwardSuccess] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await storyPointAPI.getDepartmentBoard();
      setData(res.data?.data || null);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [monthlyData, setMonthlyData] = useState<any[]>([]);

  const handleMemberClick = async (member: any) => {
    setSelectedMember(member);
    setMemberEntries([]);
    setMemberProfile(null);
    setMonthlyData([]);
    setMemberLoading(true);
    try {
      if (member.userId) {
        const [profileRes, monthlyRes] = await Promise.all([
          storyPointAPI.getUserPointsProfile(member.userId),
          storyPointAPI.getUserMonthlyBreakdown(member.userId, new Date().getFullYear()),
        ]);
        const d = profileRes.data?.data;
        setMemberProfile(d);
        setMemberEntries(d?.history || []);
        setMonthlyData(monthlyRes.data?.data?.months || []);
      }
    } catch { /* ignore */ }
    setMemberLoading(false);
  };

  const members = useMemo(() => data?.members || [], [data]);
  const filtered = useMemo(() => members.filter((m: any) =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.role?.toLowerCase().includes(search.toLowerCase())
  ), [members, search]);


  const totalMonthNet = useMemo(() => members.reduce((s: number, m: any) => s + (m.currentMonthNet || 0), 0), [members]);
  const contributors = useMemo(() => {
    if (totalMonthNet <= 0) return [];
    return members
      .filter((m: any) => (m.currentMonthNet || 0) > 0)
      .map((m: any) => ({ name: m.name, pct: Math.round(((m.currentMonthNet || 0) / totalMonthNet) * 100) }))
      .sort((a: any, b: any) => b.pct - a.pct)
      .slice(0, 6);
  }, [members, totalMonthNet]);

  const recentActivity = useMemo(() => {
    const entries: any[] = [];
    for (const m of members) {
      for (const h of (m.recentHistory || [])) {
        entries.push({ ...h, userName: m.name });
      }
    }
    return entries
      .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
      .slice(0, 8);
  }, [members]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <p className="text-sm text-gray-400">No data available</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Team Project Board</h1>
        <p className="text-sm text-gray-400 mt-0.5">{cap(data.departmentName?.toLowerCase() || '')} — Story points overview</p>
      </div>

      {/* Search */}
      <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="w-64">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        <span className="text-xs text-gray-400">{filtered.length} members</span>
      </div>

      {/* Main content */}
      <div className="flex">
        {/* Table */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Member</div>
            <div className="col-span-2 text-right">This Month</div>
            <div className="col-span-1 text-right">All Time</div>
            <div className="col-span-1 text-right">Reverted</div>
            <div className="col-span-2 text-center">Status</div>
            <div className="col-span-2 text-right">Role</div>
          </div>

          {paginated.length === 0 ? (
            <p className="text-center py-12 text-sm text-gray-400">No members found</p>
          ) : paginated.map((m: any, i: number) => {
            const rank = (page - 1) * PAGE_SIZE + i + 1;
            const isMe = m.userId === currentUserId;
            const isActive = (m.currentMonthNet || 0) > 0 || (m.netPoints || 0) > 0;
            return (
              <div key={m.userId || `${m.name}-${i}`}
                onClick={() => handleMemberClick(m)}
                className={`grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer transition-colors ${isMe ? 'bg-blue-50/30' : 'hover:bg-gray-50/80'}`}>
                <div className="col-span-1 flex items-center">
                  <span className="text-xs text-gray-300">{rank}</span>
                </div>
                <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0 font-medium">
                    {(m.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-gray-700 truncate capitalize">{m.name}</span>
                      {isMe && <span className="text-[9px] text-blue-600 bg-blue-50 px-1 py-px rounded">You</span>}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-end">
                  <span className="text-sm text-blue-600 font-semibold tabular-nums">{m.currentMonthNet ?? 0}</span>
                </div>
                <div className="col-span-1 flex items-center justify-end text-xs text-gray-500 tabular-nums">{m.netPoints || 0}</div>
                <div className={`col-span-1 flex items-center justify-end text-xs tabular-nums ${(m.totalReverted || 0) > 0 ? 'text-red-500' : 'text-gray-500'}`}>{m.totalReverted || 0}</div>
                <div className="col-span-2 flex items-center justify-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
                    {isActive ? 'Active' : 'Idle'}
                  </span>
                </div>
                <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{cap(m.role || '')}</div>
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
              <span className="text-xs text-gray-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`text-xs w-7 py-1 rounded ${p === page ? 'bg-gray-900 text-white' : 'border border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-72 border-l border-gray-100 shrink-0 overflow-y-auto">
          {/* Contribution */}
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">This month's contribution</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">Share of {totalMonthNet} SP this month</p>
            <div className="mt-3 space-y-2.5">
              {contributors.length === 0 ? (
                <p className="text-xs text-gray-400">No contributions yet</p>
              ) : contributors.map((c: any, i: number) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600 capitalize">{c.name}</span>
                    <span className="text-[10px] text-gray-400 tabular-nums">{c.pct}%</span>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent activity */}
          <div className="px-4 py-3">
            <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Recent activity</h3>
            <div className="mt-3 space-y-0">
              {recentActivity.length === 0 ? (
                <p className="text-xs text-gray-400">No recent activity</p>
              ) : recentActivity.map((e: any, i: number) => {
                const isRevert = e.action === 'revert' || e.action === 'reverted';
                return (
                  <div key={i} className="flex gap-2.5 py-2 border-b border-gray-50 last:border-0">
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${isRevert ? 'bg-red-400' : 'bg-blue-500'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-600 leading-relaxed">
                        <span className="font-medium capitalize">{e.userName}</span>
                        {' '}{isRevert ? (
                          <><span className="text-red-500">{e.points} SP reverted</span> on </>
                        ) : (
                          <>earned <span className="text-blue-600">{e.points} SP</span> on </>
                        )}
                        <span className="text-gray-400">{e.reason || 'task'}</span>
                      </p>
                      <span className="text-[10px] text-gray-400">{fmtD(e.timestamp || e.earnedAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Member detail modal */}
      {selectedMember && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMember(null)}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                  {(selectedMember.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900 capitalize">{selectedMember.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{cap(selectedMember.role || '')}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdminOrHOD && selectedMember.userId !== currentUserId && (
                  <button onClick={() => { setAwardTarget(selectedMember); setAwardPoints(''); setAwardReason(''); setAwardError(''); setShowAwardModal(true); }}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2.5 py-1 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                    Award SP
                  </button>
                )}
                <button onClick={() => setSelectedMember(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {awardSuccess && <div className="mx-5 mt-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">{awardSuccess}</div>}

            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 flex-wrap">
              <div className="flex items-center gap-1"><span className="text-sm font-semibold text-blue-600">{memberProfile?.currentMonthNet ?? selectedMember.currentMonthNet ?? 0}</span><span className="text-[10px] text-gray-400">This Month</span></div>
              <div className="w-px h-3 bg-gray-200" />
              <div className="flex items-center gap-1"><span className="text-sm font-semibold text-gray-900">{memberProfile?.netPoints ?? selectedMember.netPoints ?? 0}</span><span className="text-[10px] text-gray-400">All Time</span></div>
              <div className="w-px h-3 bg-gray-200" />
              <div className="flex items-center gap-1"><span className="text-sm font-semibold text-gray-900">{memberProfile?.totalEarned ?? selectedMember.totalEarned ?? 0}</span><span className="text-[10px] text-gray-400">Earned</span></div>
              <div className="w-px h-3 bg-gray-200" />
              <div className="flex items-center gap-1"><span className="text-sm font-semibold text-red-500">{memberProfile?.totalReverted ?? selectedMember.totalReverted ?? 0}</span><span className="text-[10px] text-gray-400">Reverted</span></div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Monthly chart */}
              {monthlyData.length > 0 && (() => {
                const maxNet = Math.max(...monthlyData.map((m: any) => m.earned || 0), 1);
                const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const currentMonth = new Date().getMonth() + 1;
                return (
                  <div className="px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{new Date().getFullYear()} Monthly Breakdown</span>
                      <span className="text-[10px] text-gray-400">
                        Total: {monthlyData.reduce((s: number, m: any) => s + (m.net || 0), 0)} net SP
                      </span>
                    </div>
                    <div className="flex items-end gap-1.5 h-28">
                      {monthlyData.map((m: any, i: number) => {
                        const earned = m.earned || 0;
                        const reverted = m.reverted || 0;
                        const barH = maxNet > 0 ? Math.max((earned / maxNet) * 100, earned > 0 ? 4 : 0) : 0;
                        const revertH = maxNet > 0 ? Math.max((reverted / maxNet) * 100, reverted > 0 ? 4 : 0) : 0;
                        const isCurrent = m.month === currentMonth;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${MONTH_LABELS[i]}: +${earned} / -${reverted} = ${m.net} net`}>
                            <div className="w-full flex flex-col items-center justify-end h-20 relative">
                              {revertH > 0 && <div className="w-full bg-red-200 rounded-t" style={{ height: `${revertH}%`, minHeight: '2px' }} />}
                              <div className={`w-full rounded-t ${isCurrent ? 'bg-blue-500' : 'bg-blue-300'}`} style={{ height: `${barH}%`, minHeight: earned > 0 ? '2px' : '0' }} />
                            </div>
                            <span className={`text-[9px] ${isCurrent ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>{MONTH_LABELS[i]}</span>
                            {earned > 0 && <span className="text-[9px] text-gray-500 tabular-nums">{m.net}</span>}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-blue-400" /><span className="text-[9px] text-gray-400">Earned</span></div>
                      <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm bg-red-200" /><span className="text-[9px] text-gray-400">Reverted</span></div>
                    </div>
                  </div>
                );
              })()}

              <div className="px-5 py-2 border-b border-gray-100">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Recent Story Points</span>
              </div>
              {memberLoading ? (
                <div className="flex items-center justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
              ) : memberEntries.length === 0 ? (
                <p className="text-center py-8 text-xs text-gray-400">No story point entries yet</p>
              ) : memberEntries.slice(0, 20).map((e: any, i: number) => {
                const isRevert = e.action === 'revert' || e.action === 'reverted';
                return (
                  <div key={i} className="flex items-center gap-2.5 px-5 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    title={`${e.reason || 'Points earned'}\n${(e.timestamp || e.earnedAt) ? new Date(e.timestamp || e.earnedAt).toLocaleString() : ''}`}>
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isRevert ? 'bg-red-400' : 'bg-blue-500'}`} />
                    <span className="text-xs text-gray-600 truncate flex-1">{e.reason || 'Task completed'}</span>
                    <span className={`text-xs font-medium shrink-0 tabular-nums ${isRevert ? 'text-red-500' : 'text-blue-600'}`}>
                      {isRevert ? '-' : '+'}{e.points || 0} SP
                    </span>
                    <span className="text-[10px] text-gray-400 shrink-0">{fmtD(e.timestamp || e.earnedAt)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Award SP Modal */}
      {showAwardModal && awardTarget && (
        <div className="!mt-0 fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowAwardModal(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-900">Award Story Points</span>
              <button onClick={() => setShowAwardModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {awardError && <div className="mx-5 mt-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{awardError}</div>}
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2.5 pb-2">
                <div className="w-7 h-7 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                  {(awardTarget.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <span className="text-sm text-gray-900 capitalize">{awardTarget.name}</span>
                  <span className="text-xs text-gray-400 ml-2">Current: {awardTarget.currentMonthNet ?? 0} SP this month</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Points to award <span className="text-red-400">*</span></label>
                <input type="number" value={awardPoints} onChange={e => setAwardPoints(e.target.value)}
                  min={1} max={100} placeholder="1-100"
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 tabular-nums ${awardError && !awardPoints ? 'border-red-300' : 'border-gray-200'}`} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Reason <span className="text-red-400">*</span></label>
                <textarea value={awardReason} onChange={e => setAwardReason(e.target.value)}
                  placeholder="e.g. Excellent client presentation, went above and beyond on Q3 deliverables..."
                  rows={3}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none ${awardError && awardReason.trim().length < 5 ? 'border-red-300' : 'border-gray-200'}`} />
                <p className="text-[10px] text-gray-400 mt-0.5">Minimum 5 characters. Your name will be appended automatically.</p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowAwardModal(false)} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
              <button onClick={async () => {
                const pts = parseInt(awardPoints);
                if (!pts || pts < 1 || pts > 100) { setAwardError('Points must be between 1 and 100'); return; }
                if (!awardReason.trim() || awardReason.trim().length < 5) { setAwardError('Reason must be at least 5 characters'); return; }
                setAwardSaving(true); setAwardError('');
                try {
                  const res = await storyPointAPI.awardPoints(awardTarget.userId, pts, awardReason.trim());
                  setShowAwardModal(false);
                  setAwardSuccess(res.data?.message || `${pts} SP awarded to ${awardTarget.name}`);
                  setTimeout(() => setAwardSuccess(''), 4000);
                  // Refresh data
                  fetchData();
                  if (selectedMember?.userId === awardTarget.userId) {
                    handleMemberClick(awardTarget);
                  }
                } catch (err: any) { setAwardError(err.response?.data?.message || 'Failed to award points'); }
                setAwardSaving(false);
              }} disabled={awardSaving}
                className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                {awardSaving ? 'Awarding...' : `Award ${awardPoints || '0'} SP`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
